import { trackEvent } from '../utils/usage.js';
// Lifetime per-player stats. House module - a near-identical copy of this
// file lives in every Around Table Round game, differing only in STATS_PATH
// and in the game's own `outcomeOf` adapter at the bottom.
//
// Bug report (2026-08-29): "It would be fun for all of the games that we've
// made if they could keep track of my stats per game."
//
// Storage, at RTDB `<game>UserStats/<uid>`:
//
//   summary/  gamesPlayed, wins, losses, ties, and the score aggregates
//   games/    { <fingerprint>: true } - the exactly-once guard
//
// The split is the point. `games` grows without bound (one key per game you
// have ever finished) and the panel never needs it, so the panel reads
// `summary` alone and a thousand-game history costs it nothing. Thunderstone
// shipped the first version of this with everything flat at the root, which
// meant its stats panel downloaded the entire history to print eight numbers;
// it now reads summary and falls back to the flat node for records written
// before the split.
//
// WHICH player is "me":
// - Online: player ids ARE Firebase uids, so it is an exact match.
// - Local: the one non-bot seat. Hotseat guests are playing on the account
//   owner's login and are nobody's lifetime record.
//
// Recording is fire-and-forget and every failure path is silent: a lost
// record costs one game of lifetime stats, and stats must never be able to
// break play.
import {
  ref as dbRef, get, set, runTransaction,
} from 'firebase/database';
import { db } from '@/firebase';

export const STATS_PATH = 'crosswordUserStats';

// The public half. `<game>UserStats/<uid>` is private to its owner, so a
// high-score table cannot be built from it - the board is a second, small,
// deliberately public row per player, holding only what a table displays.
// Matt, 2026-08-30: "it'd be cool also if we could do, like, a high scores
// table on each of these across people who play it."
export const BOARD_PATH = 'crosswordLeaderboard';

// Which end of the scale is a good game. Cartographers counts stars up; No
// Thanks! counts penalty points down. This is the one thing a copy of this
// module can get silently wrong - "best score" would just quietly report the
// worst game you ever played - so it is a named constant per game rather
// than an assumption baked into the maths.
export const LOWER_IS_BETTER = true;

const better = (held, score) => {
  if (held == null) return score;
  return LOWER_IS_BETTER ? Math.min(held, score) : Math.max(held, score);
};

const worse = (held, score) => {
  if (held == null) return score;
  return LOWER_IS_BETTER ? Math.max(held, score) : Math.min(held, score);
};

/**
 * Fold one finished game into the lifetime summary. Pure, so the whole
 * shape of the record is testable without Firebase.
 *
 * `outcome` is `{ won, tied, score, players, online, totals }` - `totals`
 * being the per-game counters, which differ from game to game.
 *
 * `won: null` means the game HAS no result: Telestrations is a party game
 * with no winner and no score, and counting every finished book as a loss
 * would be a lie the panel then has to display. A resultless game still
 * counts as played and still sums its counters.
 */
export const mergeOutcome = (summary, outcome, playedAt) => {
  const previous = summary?.totals || {};
  const totals = { ...previous };
  for (const [key, value] of Object.entries(outcome.totals || {})) {
    totals[key] = (previous[key] || 0) + value;
  }

  const scored = Number.isFinite(outcome.score);
  const judged = outcome.won !== null && outcome.won !== undefined;
  return {
    gamesPlayed: (summary?.gamesPlayed || 0) + 1,
    wins: (summary?.wins || 0) + (judged && outcome.won && !outcome.tied ? 1 : 0),
    ties: (summary?.ties || 0) + (judged && outcome.tied ? 1 : 0),
    losses: (summary?.losses || 0) + (judged && !outcome.won ? 1 : 0),
    onlineGames: (summary?.onlineGames || 0) + (outcome.online ? 1 : 0),
    // Score aggregates are kept as best/worst/sum/count rather than an
    // average, so the average stays exact as games accumulate instead of
    // drifting the way a rolling mean does.
    scoredGames: (summary?.scoredGames || 0) + (scored ? 1 : 0),
    scoreTotal: (summary?.scoreTotal || 0) + (scored ? outcome.score : 0),
    bestScore: scored ? better(summary?.bestScore, outcome.score) : (summary?.bestScore ?? null),
    worstScore: scored ? worse(summary?.worstScore, outcome.score) : (summary?.worstScore ?? null),
    lastPlayed: playedAt,
    totals,
  };
};

/**
 * A tie counts as neither a win nor a loss, so in a game that HAS results
 * `wins + losses + ties` is always `gamesPlayed`. `won` is true for every
 * seat sharing a shared victory, which is what makes that identity hold. In
 * a resultless game all three stay 0 and only `gamesPlayed` moves.
 */
export const averageScore = (summary) => (
  summary?.scoredGames ? summary.scoreTotal / summary.scoredGames : null
);

export const winRate = (summary) => (
  summary?.gamesPlayed ? (summary.wins || 0) / summary.gamesPlayed : null
);

/**
 * The summary out of a stored record, whichever shape it is in.
 *
 * Thunderstone shipped the first version of this before the summary/games
 * split and kept the counters flat at the node root. Those records are lifted
 * here rather than migrated by a separate script: a player's history is not
 * worth losing to a shape change, and the arm never fires for a node that was
 * written split in the first place - which is every other game on the hub.
 */
export const summaryOf = (record) => {
  if (!record) return null;
  if (record.summary) return record.summary;
  if (record.gamesPlayed === undefined) return null;
  const flat = { ...record };
  delete flat.games;
  return flat;
};

/**
 * Record a finished game exactly once per user.
 *
 * The transaction aborts (returns undefined) when the fingerprint is already
 * present, which is what makes a refresh, an RTDB echo, or both clients in an
 * online room observing the same finish all land as one recorded game.
 */
/**
 * Why the last save failed, if it did.
 *
 * Recording swallows every error on purpose - a lost record costs one game of
 * lifetime stats, and stats must never be able to break play. But swallowing
 * it SILENTLY is how eight of the nine games on the hub sat at zero recorded
 * games for three weeks without anyone noticing (2026-09-17): a total outage
 * and a working install look exactly alike from the outside. The failure is
 * still not thrown - it is just no longer invisible.
 */
export const statsDiagnostics = { lastError: null, lastAttemptAt: null, lastCommitted: null };

const noteFailure = (where, error) => {
  const reason = (error && (error.code || error.message)) || String(error);
  statsDiagnostics.lastError = `${where}: ${reason}`;
  console.error('[stats] ' + where + ' failed:', error);
  return false;
};

/**
 * A fingerprint is stored as a KEY under `games/`, and Firebase keys cannot
 * contain . # $ / [ or ]. Most of this hub's adapters build one by joining
 * per-player scores with a DOT ("<seed>-3.0"), which Firebase rejects - so
 * runTransaction threw on every local game and `.catch(() => false)` ate it.
 * Seven of the nine games had never recorded a single game (2026-09-17);
 * only the ones whose fingerprint happened to be dot-free ever worked.
 *
 * Sanitising HERE rather than in each adapter is deliberate: the constraint
 * belongs to the storage, not to any one game's notion of identity, and a
 * tenth game must not be able to reintroduce this by joining on a dot.
 */
const keySafe = (value) => String(value).replace(/[.#$/[\]]/g, '-');

export const recordFinishedGame = ({
  uid, name, fingerprint, outcome, now = Date.now(),
}) => {
  trackEvent('game-finished');
  statsDiagnostics.lastAttemptAt = now;
  if (!db || !uid || !fingerprint || !outcome) {
    statsDiagnostics.lastError = `not attempted: ${[!db && 'no db', !uid && 'no uid', !fingerprint && 'no fingerprint', !outcome && 'no outcome'].filter(Boolean).join(', ')}`;
    return Promise.resolve(false);
  }
  statsDiagnostics.lastError = null;
  const gameKey = keySafe(fingerprint);
  return runTransaction(dbRef(db, `${STATS_PATH}/${uid}`), (current) => {
    if (current?.games?.[gameKey]) return undefined; // already counted
    return {
      summary: mergeOutcome(summaryOf(current), outcome, now),
      games: { ...(current?.games || {}), [gameKey]: true },
    };
  }).then((result) => {
    // Only republish when the transaction actually committed - an aborted
    // one means this game was already counted and the row already says so.
    statsDiagnostics.lastCommitted = result.committed;
    if (result.committed) publishBoardRow(uid, name, result.snapshot?.val()?.summary);
    return result.committed;
  }).catch((error) => noteFailure('recordFinishedGame', error));
};

/**
 * The player's public row, rewritten from their own summary after every
 * recorded game. Fire-and-forget: a board that misses one game is a cosmetic
 * problem, and the private record is the source of truth either way.
 */
export const publishBoardRow = (uid, name, summary) => {
  if (!db || !uid || !summary) return Promise.resolve(false);
  return set(dbRef(db, `${BOARD_PATH}/${uid}`), {
    name: name || 'Someone',
    gamesPlayed: summary.gamesPlayed || 0,
    wins: summary.wins || 0,
    losses: summary.losses || 0,
    ties: summary.ties || 0,
    bestScore: summary.bestScore ?? null,
    scoredGames: summary.scoredGames || 0,
    scoreTotal: summary.scoreTotal || 0,
    totals: summary.totals || {},
    updatedAt: summary.lastPlayed || 0,
  }).then(() => true).catch((error) => noteFailure('publishBoardRow', error));
};

/**
 * Every player's row, as an array. One small row per person, so the whole
 * board is a single cheap read however long anyone's history is - which is
 * the reason it is a separate node rather than a widened read on the private
 * one.
 */
export const readBoard = async () => {
  if (!db) return [];
  try {
    const snap = await get(dbRef(db, BOARD_PATH));
    if (!snap.exists()) return [];
    return Object.entries(snap.val()).map(([uid, row]) => ({ uid, ...row }));
  } catch {
    return [];
  }
};

/**
 * Rank a board.
 *
 * `by` is 'wins', 'best', or 'played' - the last for games that have no
 * winner and no score at all (Telestrations), where turning up IS the stat.
 * Ties on the metric fall back to more games played, so somebody with one
 * lucky game does not outrank a regular on the same number.
 */
export const rankBoard = (rows, { by = 'wins', lowerIsBetter = LOWER_IS_BETTER } = {}) => {
  const scored = (rows || []).filter((row) => row.gamesPlayed > 0);
  const value = (row) => {
    if (by === 'best') return row.bestScore;
    if (by === 'played') return row.gamesPlayed || 0;
    return row.wins || 0;
  };

  const ordered = scored.filter((row) => (by !== 'best' || Number.isFinite(row.bestScore)));
  ordered.sort((a, b) => {
    const av = value(a);
    const bv = value(b);
    if (av !== bv) return (by === 'best' && lowerIsBetter) ? av - bv : bv - av;
    return (b.gamesPlayed || 0) - (a.gamesPlayed || 0);
  });

  let rank = 0;
  return ordered.map((row, i) => {
    if (i === 0 || value(row) !== value(ordered[i - 1])) rank = i + 1;
    return { ...row, rank };
  });
};

/**
 * The summary alone - never the whole history. A pre-split record has no
 * `summary` child, so that one costs a second read (once, until its owner
 * finishes another game and the write above splits it).
 */
export const readMySummary = async (uid) => {
  if (!db || !uid) return null;
  try {
    const snap = await get(dbRef(db, `${STATS_PATH}/${uid}/summary`));
    if (snap.exists()) return snap.val();
    const whole = await get(dbRef(db, `${STATS_PATH}/${uid}`));
    return whole.exists() ? summaryOf(whole.val()) : null;
  } catch {
    return null;
  }
};

// -------------------------------------------------------------- the adapter

/**
 * A crossword has no opponent: "won" means solved CLEAN - no revealed
 * squares - and the score is the solve time in seconds, so LOWER_IS_BETTER
 * is true here (the only other game on the hub where it is: No Thanks!).
 * A solve with reveals still counts as played and keeps its time; it just
 * isn't a win. Solving together counts as online.
 */
export const outcomeOf = ({ puzzle, seconds, reveals, roomMeta, myId }) => {
  if (!puzzle || !myId) return null;
  const revealed = Object.keys(reveals || {}).length;
  const clean = revealed === 0;
  const together = Boolean(roomMeta);
  return {
    won: clean,
    tied: false,
    score: Math.max(1, Math.round(seconds || 0)),
    players: together ? Object.keys(roomMeta.players || {}).length : 1,
    online: together,
    totals: {
      solved: 1,
      clean: clean ? 1 : 0,
      revealed,
      together: together ? 1 : 0,
    },
  };
};

export const fingerprintOf = ({ puzzle, roomId }) => {
  if (roomId) return `room-${roomId}`;
  if (!puzzle) return null;
  return `solo-${puzzle.date}`;
};
